import React, { useEffect, useRef, useState } from 'react'
import './Search.css'
import { useTranslation } from 'react-i18next'

const SearchBox = ({ onLocationSelect }) => {
  const mapRef = useRef(null)
  const searchRef = useRef(null)
  const [coordinates, setCoordinates] = useState(null)
  const [map, setMap] = useState(null)
  const [searchResult, setSearchResult] = useState(null)
  const [nearbySuggestions, setNearbySuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [searchValue, setSearchValue] = useState('')

  // Your API key
  const apiKey = 'AIzaSyBrgEgThZMUGNsYDydYIXQ53VAbmx5tu-s'

  const { t } = useTranslation()

  useEffect(() => {
    // Create a window callback for Google Maps to call when loaded
    window.initGoogleMaps = () => {
      initMap()
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`
    script.async = true
    script.defer = true

    script.onerror = () => {
      console.error('Google Maps script failed to load')
      setError(
        'Failed to load Google Maps. Please check your internet connection.'
      )
    }

    document.body.appendChild(script)

    return () => {
      window.initGoogleMaps = null
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  // Find nearby places based on user's search text or current map center
  const findNearbySuggestions = searchText => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setError(
        'Places API not available. Please check API key and enabled services.'
      )
      return
    }

    setIsLoading(true)
    setError(null)

    // First, try to use TextSearch which works better with partial or incorrect location names
    const service = new window.google.maps.places.PlacesService(mapRef.current)

    const textSearchRequest = {
      query: searchText,
      fields: ['name', 'geometry', 'formatted_address']
    }

    service.textSearch(textSearchRequest, (results, status) => {
      setIsLoading(false)

      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        results &&
        results.length > 0
      ) {
        console.log('Found nearby suggestions through text search:', results)
        setNearbySuggestions(results.slice(0, 5))
      } else {
        console.warn('Text search failed with status:', status)

        // Fall back to nearby search based on default location if text search fails
        const defaultLocation = new window.google.maps.LatLng(
          37.7749,
          -122.4194
        ) // San Francisco as default

        const nearbyRequest = {
          location: map ? map.getCenter() : defaultLocation,
          radius: '5000', // 5km radius
          type: ['establishment']
        }

        service.nearbySearch(nearbyRequest, (nearbyResults, nearbyStatus) => {
          if (
            nearbyStatus === window.google.maps.places.PlacesServiceStatus.OK &&
            nearbyResults
          ) {
            console.log(
              'Found nearby suggestions through nearby search:',
              nearbyResults
            )
            setNearbySuggestions(nearbyResults.slice(0, 5))
          } else {
            console.error(
              'Nearby search also failed with status:',
              nearbyStatus
            )
            setError(
              'Could not find suggestions. Please try a different search term.'
            )
            setNearbySuggestions([])
          }
        })
      }
    })
  }

  // Handle when user selects a suggestion
  const handleSuggestionClick = place => {
    if (!place.geometry || !place.geometry.location) {
      console.error('Invalid place data:', place)
      return
    }

    // Get location object from place
    const loc = place.geometry.location

    // Extract coordinates
    const newCoordinates = {
      lat: loc.lat(),
      lng: loc.lng()
    }

    console.log('Selected coordinates:', newCoordinates)

    // Update search input with the exact original value
    if (searchRef.current) {
      searchRef.current.value = place.name
      setSearchValue(place.name)
    }

    // Call onLocationSelect with the location data
    if (onLocationSelect) {
      onLocationSelect({
        name: place.name,
        address: place.formatted_address || place.vicinity || '',
        coordinates: newCoordinates,
        geometry: place.geometry
      })
    }

    // Update state with new coordinates
    setCoordinates(newCoordinates)

    // Set selected location with exact original value
    setSelectedLocation({
      name: place.name,
      address: place.formatted_address || place.vicinity || '',
      coordinates: newCoordinates
    })

    // Update marker position if map exists
    if (map && window.currentMarker) {
      map.panTo(loc)
      map.setZoom(15)
      window.currentMarker.setPosition(loc)
    }

    // Store search result with exact original value
    setSearchResult({
      name: place.name,
      coordinates: newCoordinates,
      address: place.formatted_address || place.vicinity || ''
    })

    // Clear suggestions after selection
    setNearbySuggestions([])
  }

  const initMap = () => {
    if (!window.google || !window.google.maps) {
      setError('Google Maps API failed to load. Please refresh the page.')
      return
    }

    // Create a hidden map div for API functionality but don't display it
    const hiddenMapDiv = document.createElement('div')
    hiddenMapDiv.style.display = 'none'
    document.body.appendChild(hiddenMapDiv)

    const initialCenter = { lat: 37.7749, lng: -122.4194 } // San Francisco

    const mapInstance = new window.google.maps.Map(hiddenMapDiv, {
      center: initialCenter,
      zoom: 12
    })

    setMap(mapInstance)

    // Store marker globally
    window.currentMarker = new window.google.maps.Marker({
      map: mapInstance,
      position: initialCenter
    })

    // Setup Places Autocomplete
    if (window.google.maps.places && searchRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        searchRef.current,
        {
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: 'np' },
          fields: ['name', 'formatted_address', 'geometry', 'vicinity']
        }
      )

      autocomplete.bindTo('bounds', mapInstance)

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        if (!place.geometry || !place.geometry.location) {
          // Location not found - find nearby suggestions based on search text
          const searchText = searchRef.current.value
          console.log(
            'Location not found, showing suggestions for:',
            searchText
          )
          findNearbySuggestions(searchText)
          return
        }

        const loc = place.geometry.location

        // Extract coordinates
        const newCoordinates = {
          lat: loc.lat(),
          lng: loc.lng()
        }

        console.log('Autocomplete coordinates:', newCoordinates)

        // Update coordinates state
        setCoordinates(newCoordinates)

        // Update map and marker in background
        mapInstance.panTo(loc)
        mapInstance.setZoom(15)
        window.currentMarker.setPosition(loc)

        // Set selected location with exact original value
        setSelectedLocation({
          name: place.name,
          address: place.formatted_address || '',
          coordinates: newCoordinates
        })

        // Update search result with exact original value
        setSearchResult({
          name: place.name,
          coordinates: newCoordinates,
          address: place.formatted_address || ''
        })

        // Update search input with exact original value
        if (searchRef.current) {
          searchRef.current.value = place.name
          setSearchValue(place.name)
        }

        // Clear suggestions
        setNearbySuggestions([])
      })
    } else {
      setError('Google Places library not loaded properly')
    }

    // Create a hidden div for Places Service
    const placesDiv = document.createElement('div')
    placesDiv.id = 'placesServiceDiv'
    placesDiv.style.display = 'none'
    document.body.appendChild(placesDiv)
    mapRef.current = placesDiv // Use this for PlacesService reference
  }

  // Handle manual search button click
  const handleSearch = () => {
    if (!searchRef.current || !searchRef.current.value.trim()) {
      setError('Please enter a search term')
      return
    }

    const searchText = searchRef.current.value
    setSearchValue(searchText) // Store the exact search text
    findNearbySuggestions(searchText)
  }

  const handleClearSearch = () => {
    if (searchRef.current) {
      searchRef.current.value = ''
      setSearchValue('')
      setNearbySuggestions([])
      setError(null)
    }
  }

  const handleSearchChange = e => {
    const value = e.target.value
    setSearchValue(value)
  }

  // Cleanup function
  useEffect(() => {
    return () => {
      const placesDiv = document.getElementById('placesServiceDiv')
      if (placesDiv) {
        document.body.removeChild(placesDiv)
      }

      const hiddenMapDiv = document.querySelector('div[style="display: none;"]')
      if (hiddenMapDiv) {
        document.body.removeChild(hiddenMapDiv)
      }
    }
  }, [])

  return (
    <div className="search-container">
      <div className="search-box">
        <div className="search-input-wrapper">
          <input
            ref={searchRef}
            type="text"
            placeholder={t('v5:search_for_a_location')}
            className="search-input"
            onKeyPress={e => e.key === 'Enter' && handleSearch()}
            onChange={handleSearchChange}
            value={searchValue}
          />
          {searchValue && (
            <button className="clear-button" onClick={handleClearSearch}>
              ×
            </button>
          )}
        </div>
        <button onClick={handleSearch} className="search-button">
          {t('v5:search')}
        </button>
      </div>

      {isLoading && (
        <div className="loading">{t('v5:loading_suggestions')}</div>
      )}
      {error && <div className="error">{error}</div>}

      {nearbySuggestions.length > 0 && (
        <div className="suggestions">
          <h3>{t('v5:suggested_locations')}</h3>
          <ul>
            {nearbySuggestions.map((place, index) => (
              <li
                key={index}
                onClick={() => handleSuggestionClick(place)}
                className="suggestion-item"
              >
                <div className="place-name">{place.name}</div>
                <div className="place-address">
                  {place.formatted_address || place.vicinity || ''}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default SearchBox
